import * as React from 'react';

import { TextField, Autocomplete, Grid, Typography } from '@mui/material';

import { MapPin } from 'react-feather';

import { debounce } from '@mui/material/utils';
import { useGoogleMaps } from '@pv/common/providers';

const autocompleteService = { current: null };

interface PlaceType {
  description: string;
}

interface LocationAutocompleteProps {
  locationQuery: string;
  onChange: (searchString: string) => void;
  styleProps?: any;
  size?: 'small' | 'medium';
}
export const LocationAutocomplete = function ({
  locationQuery,
  onChange,
  styleProps,
  size = 'medium',
}: LocationAutocompleteProps) {
  const { isLoaded } = useGoogleMaps();
  const [value, setValue] = React.useState<PlaceType | null>({
    description: locationQuery,
  });
  const [inputValue, setInputValue] = React.useState(locationQuery);
  const [options, setOptions] = React.useState<readonly PlaceType[]>([]);

  React.useEffect(() => {
    setValue({ description: locationQuery });
    setInputValue(locationQuery);
  }, [locationQuery]);

  const fetch = React.useMemo(
    () =>
      debounce(
        (
          request: { input: string },
          callback: (results?: readonly PlaceType[]) => void,
        ) => {
          (autocompleteService.current as any).getPlacePredictions(
            {
              ...request,
              types: [
                'neighborhood',
                'locality',
                'administrative_area_level_2',
                'administrative_area_level_3',
                'sublocality',
              ],
              region: 'US',
              componentRestrictions: {
                country: ['us', 'ca', 'mx', 'pr', 'vi', 'gu', 'mp', 'as'],
              }, // restrict to US, CA, MX, Puerto Rico, US Virgin Islands, Guam, Northern Mariana Islands, and American Samoa
            },
            callback,
          );
        },
        400,
      ),
    [],
  );

  React.useEffect(() => {
    let active = true;

    // Re-runs when the Maps script arrives, so suggestions start without
    // waiting for the next keystroke.
    if (!autocompleteService.current && isLoaded) {
      autocompleteService.current = new (
        window as any
      ).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results?: readonly PlaceType[]) => {
      if (active) {
        let newOptions: readonly PlaceType[] = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch, isLoaded]);

  return (
    <Autocomplete
      id="google-place-search"
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      size={size}
      includeInputInList
      filterSelectedOptions
      value={value}
      fullWidth
      forcePopupIcon={false}
      noOptionsText="Enter a location"
      onChange={(event: any, newValue: PlaceType | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        onChange(newValue?.description || '');
      }}
      isOptionEqualToValue={(option, value) => {
        // strip commas before comparing
        return (
          option.description.replace(',', '') ===
          value.description.replace(',', '')
        );
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          InputLabelProps={{ shrink: true }}
          label="Where"
          fullWidth
          placeholder="Anywhere"
          variant="outlined"
          sx={{ ...styleProps }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;

        return (
          <li key={key} {...optionProps}>
            <Grid container sx={{ alignItems: 'center' }}>
              <Grid item sx={{ display: 'flex', width: 44 }}>
                <MapPin />
              </Grid>
              <Grid
                item
                sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}
              >
                <Typography variant="body2" color="text.secondary">
                  {option.description}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
};
